import type { OrderSummaryDTO } from "@/modules/orders/domain/contracts";
import { formatPriceLak } from "@/shared/lib/format-price";
import { Card, CardContent } from "@devhop/ui";
import {
  CheckCircleIcon,
  ClockIcon,
  PackageIcon,
  ShoppingCartIcon,
} from "lucide-react";
import { useOrderSummaryQuery } from "../api/queries";

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
}) {
  return (
    <Card className="p-0">
      <CardContent className="flex items-center gap-4 p-4">
        <div className="rounded-md border bg-muted/50 p-2">
          <Icon className="size-5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-muted-foreground text-sm">{label}</p>
          <p className="font-semibold text-xl">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function OrderSummaryCard() {
  const { data, isLoading } = useOrderSummaryQuery();

  if (isLoading || !data) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-md border bg-muted/50 p-2">
                <div className="size-5 animate-pulse rounded bg-muted" />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">—</p>
                <p className="font-semibold text-xl">—</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const s = data as OrderSummaryDTO;
  const inProgress =
    (s.byStatus.confirmed ?? 0) +
    (s.byStatus.packing ?? 0) +
    (s.byStatus.shipped ?? 0);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard icon={ShoppingCartIcon} label="ລວມທັງໝົດ" value={s.total} />
      <StatCard
        icon={ClockIcon}
        label="ລໍຖ້າຊຳລະ"
        value={s.byStatus.pending_payment ?? 0}
      />
      <StatCard icon={PackageIcon} label="ກຳລັງດຳເນີນການ" value={inProgress} />
      <StatCard
        icon={CheckCircleIcon}
        label="ຍອດລວມ (LAK)"
        value={formatPriceLak(s.totalAmount)}
      />
    </div>
  );
}
